import React from "react";
import Layout from "../layout/Layout";
import AlertBanner from "./AlertBanner";
import { Alert } from "../ui/Alert";
import { InfoCircle } from "../icons";

interface PageStateGuardProps {
  /** Whether data is currently being loaded. */
  loading: boolean;
  /** An error message to display, or null when there is no error. */
  error: string | null;
  /**
   * A guest-facing reason the booking cannot be used — DMN's own wording for a
   * booking it refuses, or our copy for one that is already completed. Shown as
   * an informational banner ahead of the error state, because it is an expected
   * outcome rather than a failure and retrying will not help.
   */
  refusalMessage?: string | null;
  /** The primary data object — when falsy the "no data" state is shown. */
  data: unknown;
  /** Text shown when there is no data. Defaults to "No booking found." */
  noDataMessage?: string;
  /** Optional action rendered below the error message (e.g. a retry button). */
  errorAction?: React.ReactNode;
  /** Whether to show the BookingHeader in fallback Layout states. */
  showHeader?: boolean;
  /** Content to render once loading, error and no-data checks pass. */
  children: React.ReactNode;
}

/**
 * Wraps the common loading → refusal → error → no-data → children guard
 * pattern used by secureYourBooking, placeYourOrder and manageYourOrder.
 *
 * Each guard state renders within a `<Layout>` so the page chrome is
 * always present.
 */
const PageStateGuard: React.FC<PageStateGuardProps> = ({
  loading,
  error,
  refusalMessage,
  data,
  noDataMessage = "No booking found.",
  errorAction,
  showHeader = false,
  children,
}) => {
  if (loading) {
    return (
      <Layout
        showHeader={showHeader}
        main={
          <div className="app-main">
            <div className="loading-spinner"></div>
          </div>
        }
        aside={null}
      />
    );
  }

  if (refusalMessage) {
    return (
      <Layout
        showHeader={showHeader}
        main={
          <div className="app-main">
            <AlertBanner variant="warning" icon={<InfoCircle color="#9a3412" size={16} />} className="info-alert">
              {refusalMessage}
            </AlertBanner>
          </div>
        }
        aside={null}
      />
    );
  }

  if (error) {
    return (
      <Layout
        showHeader={showHeader}
        main={
          <div className="app-main">
            <Alert variant="danger" className="gcw:flex gcw:items-start">
              Error: {error}
            </Alert>
            {errorAction}
          </div>
        }
        aside={null}
      />
    );
  }

  if (!data) {
    return (
      <Layout
        showHeader={showHeader}
        main={
          <div className="app-main">
            <Alert variant="warning" className="gcw:flex gcw:items-start">
              {noDataMessage}
            </Alert>
          </div>
        }
        aside={null}
      />
    );
  }

  return <>{children}</>;
};

export default PageStateGuard;
